import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { HttpClient } from 'src/core/common/domain/http/http-client';
import { Location, Task } from 'src/core/master-data/domain/task';
import { TaskRepository } from 'src/core/tasks/domain/task-repository';
import { LocalDate } from 'src/core/common/domain/date/local-date';
import { Timezone } from 'src/core/common/domain/date/timezone';
import { FilesystemService } from 'src/core/filesystem/domain/filesystem.service';
import { LocalAttachment } from 'src/core/attachment/domain/local-attachment';
import { LocalFileAttachment } from 'src/core/attachment/domain/local-file-attachment';
import { Attachment } from 'src/core/attachment/domain/attachment';
import { Nullable } from 'src/core/common/domain/types/types';
import { ApiService } from 'src/core/api/domain/api.service';
import { ExceptionManagerService } from 'src/core/common/domain/exceptions/exception-manager.service';
import { ApiAccessService } from 'src/core/common/domain/api-access/api-access.service';
import { ApiSaveTaskAttachmentRequest, ApiSaveTaskRequest } from './api-save-task-request';
import { ApiCloseTaskRequest } from './api-close-task-request';
import { AttachmentHelper } from 'src/tests/core/common/domain/attachment/attachment.helper';
import { ApiRejectTaskRequest } from './api-reject-task-request';
import { ApiCreateTaskRequest } from './api-create-task-request';
import { MasterDataService } from 'src/core/master-data/domain/master-data-service';
import { WorkType } from 'src/core/master-data/domain/work-type';
import { RequestTooLargeException } from 'src/core/common/domain/exceptions/request-too-large-exception';
import { UnitHelper } from 'src/core/common/domain/units/unit-helper';
import { TranslatorService } from 'src/core/translator/domain/translator-service';
import { MasterDataConfiguration } from 'src/core/master-data/domain/master-data-configuration';
import { ApiTaskResponse } from 'src/core/master-data/infrastructure/api-task-response';
import { availableActionsFromApi } from 'src/core/master-data/infrastructure/api-available-actions';
import { ConnectivityService } from 'src/core/connection/domain/connectivity.service';
import { NoConnectionException } from 'src/core/common/domain/exceptions/no-connection-exception';
import { ApiTasksLoadResponse } from 'src/core/master-data/infrastructure/api-tasks-load-response';
import { ApiTaskGetResponse } from 'src/core/master-data/infrastructure/api-task-get-response';
import { TaskNotFoundException } from 'src/core/common/domain/exceptions/task-not-found-exception';
import { GeolocationHelper } from 'src/core/common/domain/geolocation/geolocation-helper';
import { ApiConfirmRejectTaskRequest } from './api-confirm-reject-task-request';
import { ApiConfirmCloseTaskRequest } from './api-confirm-close-task-request';

@Injectable({
  providedIn: 'any',
})
export class HttpTaskRepository extends TaskRepository {
  constructor(
    private readonly apiAccessService: ApiAccessService,
    private readonly apiService: ApiService,
    private readonly client: HttpClient,
    private readonly connectivityService: ConnectivityService,
    private readonly exceptionManager: ExceptionManagerService,
    private readonly filesystem: FilesystemService,
    private readonly masterData: MasterDataService,
    private readonly translation: TranslatorService
  ) {
    super();
  }

  async load(ambitCode: string = null): Promise<Array<Task>> {
    try {
      if (!this.connectivityService.isOnline()) {
        throw new NoConnectionException();
      }

      await this.apiAccessService.renewApiAccessIfNeeded();
      const parameters = {
        ambit_code: ambitCode,
        // date_from: LocalDate.fromString('2024-02-01').ymd, // 2010-01-01
        // date_to: LocalDate.fromString('2024-02-29').ymd, // 2090-01-01
        // search: undefined,
        // work_types: undefined,
        // status_list: undefined,
        // assignee: undefined,
        // page: 1,
        // page_size: 100_000,
      };
      // remove empty parameters (whose value is undefined and null)
      const filteredParameters = Object.fromEntries(
        Object.entries(parameters).filter(([key, value]) => key !== undefined && value !== undefined && value !== null)
      );

      const queryString = new URLSearchParams(filteredParameters).toString();
      const url = `${this.apiService.baseUrl()}/tasks.list?${queryString}`;
      const response = await this.client.get<ApiTasksLoadResponse>(url, {}, {}, true, true);

      const tasks: Array<Task> = this.buildTasksFromApiResponse(response.tasks, this.masterData.getConfiguration());
      return tasks;
    } catch (exception) {
      throw this.exceptionManager.manage(exception);
    }
  }

  async get(id: string): Promise<Task> {
    try {
      if (!this.connectivityService.isOnline()) {
        throw new NoConnectionException();
      }

      await this.apiAccessService.renewApiAccessIfNeeded();
      const url = `${this.apiService.baseUrl()}/tasks.get?id=${encodeURIComponent(id)}`;
      const response = await this.client.get<ApiTaskGetResponse>(url, {}, {}, true, true);

      const task = this.buildTaskFromApiResponse(response.task, this.masterData.getConfiguration());
      return task;
    } catch (exception) {
      // The global exception manager maps any HTTP 404 to AmbitNotFoundException, so we must
      // intercept the "task not found" case here before delegating.
      if (exception instanceof HttpErrorResponse && exception.status === HttpStatusCode.NotFound) {
        throw new TaskNotFoundException();
      }
      throw this.exceptionManager.manage(exception);
    }
  }

  async start(task: Task): Promise<void> {
    try {
      if (!this.connectivityService.isOnline()) {
        throw new NoConnectionException();
      }

      await this.apiAccessService.renewApiAccessIfNeeded();
      const payload = {
        id: task.id,
        started_at: LocalDate.now().atom, // it should be the task started date (from Task class)
      };
      await this.client.post(`${this.apiService.baseUrl()}/task.start`, payload, {}, true, true);
    } catch (exception) {
      throw this.exceptionManager.manage(exception);
    }
  }

  async stop(task: Task): Promise<void> {
    try {
      if (!this.connectivityService.isOnline()) {
        throw new NoConnectionException();
      }

      await this.apiAccessService.renewApiAccessIfNeeded();
      const payload = {
        id: task.id,
        stopped_at: LocalDate.now().atom, // it should be the task started date (from Task class)
      };
      await this.client.post(`${this.apiService.baseUrl()}/task.stop`, payload, {}, true, true);
    } catch (exception) {
      throw this.exceptionManager.manage(exception);
    }
  }

  async close(task: Task): Promise<void> {
    try {
      if (!this.connectivityService.isOnline()) {
        throw new NoConnectionException();
      }

      await this.apiAccessService.renewApiAccessIfNeeded();
      const extraData = task.getCloseTaskData();
      const signatureAttachmentContent = extraData?.signature
        ? AttachmentHelper.removeBase64Header((await this.getAttachmentContent(extraData?.signature)).base64_content)
        : null;
      const payload: ApiCloseTaskRequest = {
        id: task.id,
        closed_at: task.closedAt?.atom ?? null,
        observations_for_client: task.observationsForClient ?? null,
        emails: extraData?.emails ?? null,
        predefined_observations: extraData?.predefinedObservations ?? null,
        resolution: extraData?.resolutions ?? null,
        signature_type: extraData?.signatureType ?? null,
        signature: signatureAttachmentContent,
      };
      await this.ensureMaxRequestSizeIsNotTooLarge(JSON.stringify(payload));
      await this.client.post(`${this.apiService.baseUrl()}/task.close`, payload, {}, true, true);
    } catch (exception) {
      throw this.exceptionManager.manage(exception);
    }
  }

  async confirmClose(task: Task): Promise<void> {
    try {
      if (!this.connectivityService.isOnline()) {
        throw new NoConnectionException();
      }

      await this.apiAccessService.renewApiAccessIfNeeded();
      const payload: ApiConfirmCloseTaskRequest = {
        id: task.id,
        confirm_closed_at: task.confirmClosedAt?.atom ?? null,
        observations_for_client: task.getConfirmCloseTaskData().observationsForClient ?? null,
        resolution: task.getConfirmCloseTaskData()?.resolution ?? null,
      };
      await this.client.post(`${this.apiService.baseUrl()}/task.confirm-close`, payload, {}, true, true);
    } catch (exception) {
      throw this.exceptionManager.manage(exception);
    }
  }

  async reject(task: Task): Promise<void> {
    try {
      if (!this.connectivityService.isOnline()) {
        throw new NoConnectionException();
      }

      await this.apiAccessService.renewApiAccessIfNeeded();
      const extraData = task.getRejectTaskData();
      const payload: ApiRejectTaskRequest = {
        id: task.id,
        rejected_at: task.rejectedAt?.atom ?? null,
        predefined_observations: extraData?.predefinedObservations ?? null,
        resolution: extraData?.resolutions ?? null,
        observations_for_client: task.observationsForClient ?? null,
      };
      await this.client.post(`${this.apiService.baseUrl()}/task.reject`, payload, {}, true, true);
    } catch (exception) {
      throw this.exceptionManager.manage(exception);
    }
  }

  async confirmReject(task: Task): Promise<void> {
    try {
      if (!this.connectivityService.isOnline()) {
        throw new NoConnectionException();
      }

      await this.apiAccessService.renewApiAccessIfNeeded();
      const payload: ApiConfirmRejectTaskRequest = {
        id: task.id,
        confirm_rejected_at: task.confirmRejectedAt?.atom ?? null,
        observations_for_client: task.getConfirmRejectTaskData().observationsForClient ?? null,
        resolution: task.getConfirmRejectTaskData()?.resolution ?? null,
      };
      await this.client.post(`${this.apiService.baseUrl()}/task.confirm-reject`, payload, {}, true, true);
    } catch (exception) {
      throw this.exceptionManager.manage(exception);
    }
  }

  async create(data: {
    createdAt: LocalDate;
    expectedDate: LocalDate;
    ambitId: string;
    taskTypeId: string;
    workTypeId: string;
    priorityId?: number;
    subject: string;
    attachments: Array<Attachment>;
  }): Promise<{ taskId: string }> {
    try {
      if (!this.connectivityService.isOnline()) {
        throw new NoConnectionException();
      }

      await this.apiAccessService.renewApiAccessIfNeeded();

      // If expected date is today, we send the current time, otherwise we send at start_of_day (00:00:00)
      const expectedDate = data.expectedDate.ymd === data.createdAt.ymd ? data.createdAt.atom : data.expectedDate.atom;
      const attachments = await this.getAttachmentsContent(data.attachments);

      const payload: ApiCreateTaskRequest = {
        expected_date: expectedDate,
        created_at: data.createdAt.atom,
        ambit_id: data.ambitId,
        task_type_id: data.taskTypeId,
        work_type_id: data.workTypeId,
        priority: data.priorityId,
        subject: data.subject,
        attachments: attachments.length > 0 ? attachments : undefined,
      };
      await this.ensureMaxRequestSizeIsNotTooLarge(JSON.stringify(payload));
      const response = await this.client.post<{
        task_id: string;
      }>(`${this.apiService.baseUrl()}/task.create`, payload, {}, true, true);
      const taskId = response.task_id;
      return { taskId };
    } catch (exception) {
      throw this.exceptionManager.manage(exception);
    }
  }

  async save(task: Task): Promise<void> {
    try {
      // we don't save if we have no attachments
      if (task.attachments.length === 0) {
        return;
        return;
      }

      if (!this.connectivityService.isOnline()) {
        throw new NoConnectionException();
      }

      await this.apiAccessService.renewApiAccessIfNeeded();
      const attachments: Array<ApiSaveTaskAttachmentRequest> = await this.getAttachmentsContent(task.attachments);
      const payload: ApiSaveTaskRequest = {
        id: task.id,
        updated_at: task.updatedAt?.atom ?? LocalDate.now().atom,
        attachments,
      };
      await this.ensureMaxRequestSizeIsNotTooLarge(JSON.stringify(payload));
      await this.client.post(`${this.apiService.baseUrl()}/task.save`, payload, {}, true, true);
    } catch (exception) {
      throw this.exceptionManager.manage(exception);
    }
  }

  private buildTasksFromApiResponse(
    tasks: Array<ApiTaskResponse>,
    configuration: MasterDataConfiguration
  ): Array<Task> {
    return tasks.map((task) => this.buildTaskFromApiResponse(task, configuration));
  }

  private buildTaskFromApiResponse(task: ApiTaskResponse, configuration: MasterDataConfiguration) {
    const taskDate = {
      expected: task.planned_date.expected,
      from: task.planned_date.valid_range_start,
      to: task.planned_date.valid_range_end,
      timezone: Timezone.firstSupported(task.timezone, configuration.getTimezone()),
    };
    const taskLocation: Location = {
      full_address: task.location.full_address,
      geolocation: GeolocationHelper.parseFromAPI(task.location),
    };

    const workType = this.resolveWorkType(task.work_type);
    const taskType = task.task_type ? { id: task.task_type.id, name: task.task_type.name } : null;
    const priority = task.priority ? { id: task.priority.id, name: task.priority.name } : null;
    const route = task.route ? { id: task.route.id, name: task.route.name } : null;
    const displayServiceAddressInTask = configuration.isDisplayServiceAddressInTask();
    const displayPostalAddressInImmediateTask = configuration.isDisplayPostalAddressInImmediateTask();
    const displayPostalAddressInPlannedTask = configuration.isDisplayPostalAddressInPlannedTask();
    const displayAmbitInImmediateTask = configuration.isDisplayAmbitInImmediateTask();
    const displayAmbitInPlannedTask = configuration.isDisplayAmbitInPlannedTask();
    const displayDescriptionInImmediateTask = configuration.isDisplayDescriptionInImmediateTask();
    const displayDescriptionInPlannedTask = configuration.isDisplayDescriptionInPlannedTask();
    const displayInlineObservationsForClient = configuration.isDisplayInlineObservationsForClient();

    return Task.fromPrimitives({
      id: task.id,
      status: task.status,
      subject: task.subject,
      description: task.description,
      observations_for_client: task.observations_for_client,
      description_files: task.description_files ?? [],
      work_type: workType.toPrimitives(),
      task_type: taskType,
      priority,
      route,
      ambit: task.ambit,
      date: taskDate,
      assignee: task.assignee,
      frequency: task.frequency,
      resolution: task.resolution ?? null,
      must_be_modified_by_assignee: task.must_be_modified_by_assignee,
      location: taskLocation,
      started_at: task.started_at,
      updated_at: task.updated_at,
      closed_at: task.closed_at,
      confirm_closed_at: null,
      rejected_at: null,
      confirm_rejected_at: null,
      created_at: task.created_at,
      attachments: [],
      display_service_address_in_task: displayServiceAddressInTask,
      display_postal_address_in_immediate_task: displayPostalAddressInImmediateTask,
      display_postal_address_in_planned_task: displayPostalAddressInPlannedTask,
      display_ambit_in_immediate_task: displayAmbitInImmediateTask,
      display_ambit_in_planned_task: displayAmbitInPlannedTask,
      display_description_in_immediate_task: displayDescriptionInImmediateTask,
      display_description_in_planned_task: displayDescriptionInPlannedTask,
      display_inline_observations_for_client: displayInlineObservationsForClient,
      close_task_data: null,
      reject_task_data: null,
      confirm_close_task_data: null,
      confirm_reject_task_data: null,
      in_final_state: false,
      available_actions: availableActionsFromApi(task.available_actions),
    });
  }

  /*
   * A task's payload carries only the work type id and name; whether it is planned and where it
   * sorts live in the master data catalog, so the task's work type is completed from there. An id
   * the catalog does not know keeps its own name and falls back to the last position.
   */
  private resolveWorkType(payload: { id: string; name?: string }): WorkType {
    const catalogued = this.masterData.findWorkType(payload.id);
    if (catalogued) {
      return catalogued;
    }

    this.applyWorkTypeNameApiFallback(payload);
    return WorkType.fromPrimitives({ id: payload.id, name: payload.name });
  }

  // TODO: Extract duplicated method or delete
  private applyWorkTypeNameApiFallback(workType: { id: string; name?: string }) {
    if (!workType.name) {
      const key = `FILTERS.WORK_TYPES.${workType.id.toUpperCase()}`;
      let workTypeName = this.translation.translate(key);
      if (workTypeName === key) {
        workTypeName = workType.id;
      }
      workType.name = workTypeName;
    }
  }

  private async getAttachmentsContent(
    attachmentsCollection: Array<Attachment>
  ): Promise<Array<ApiSaveTaskAttachmentRequest>> {
    const attachments: Array<ApiSaveTaskAttachmentRequest> = [];

    for (const attachment of attachmentsCollection) {
      const attachmentContent = await this.getAttachmentContent(attachment);
      if (attachmentContent) {
        attachments.push({
          filename: attachment.filename,
          base64_content: AttachmentHelper.removeBase64Header(attachmentContent.base64_content),
        });
      }
    }

    return attachments;
  }

  private async getAttachmentContent(
    attachment: Attachment
  ): Promise<Nullable<{ id: string; filename: string; base64_content: string }>> {
    let base64Content = null;
    if (attachment instanceof LocalAttachment) {
      base64Content = attachment.content;
    } else if (attachment instanceof LocalFileAttachment) {
      base64Content = await this.filesystem.readAsDataURL(attachment.getUrl());
    }
    if (base64Content === null) {
      return null;
    }

    return {
      id: attachment.id,
      filename: attachment.filename,
      base64_content: base64Content,
    };
  }

  private async ensureMaxRequestSizeIsNotTooLarge(payload: string): Promise<void> {
    const requestSize = payload.length;
    // …output bytes per input byte converges to 4 / 3 or 1.33333 for large n
    // E.g.: A request of 7 MB will actually weigh 9.333 MB
    //       7 MB * 4/3  = 9.333 MB
    const base64EncodeFactor = 4 / 3;
    const encodedRequestSize = requestSize * base64EncodeFactor;
    const maxRequestSize = this.masterData.getConfiguration().getMaxRequestSize();

    if (encodedRequestSize > maxRequestSize) {
      const encodedRequestSizeHumanReadable = UnitHelper.humanReadable(encodedRequestSize);
      const maxRequestSizeHumanReadable = UnitHelper.humanReadable(maxRequestSize);

      throw new RequestTooLargeException(
        this.translation.translate('MESSAGES.REQUEST_TOO_LARGE', {
          request_size: encodedRequestSizeHumanReadable,
          max_request_size: maxRequestSizeHumanReadable,
        })
      );
    }
  }
}
